import { FC, useMemo } from 'react';
import {
  useGetRevenueSchedulePeriods,
  useGetRevenueScheduleSubscriptionItems,
  useSearchRevenueSchedules,
} from '../../../api/revenueService';
import { MBox, MPageLoader, MText } from '../../../components/Monetize';
import { RevenueContractSummaryTable } from '../../../components/RevenueRecognition/RevenueContractSummaryTable';

interface RevenueContractSummaryTabProps {
  revenueContractId: string;
}

export const RevenueContractSummaryTab: FC<RevenueContractSummaryTabProps> = ({
  revenueContractId,
}) => {
  const { data, isLoading } = useSearchRevenueSchedules(
    { revenueContractId },
    { enabled: !!revenueContractId, refetchOnWindowFocus: false },
  );

  const latestSchedule = useMemo(() => {
    const schedules = data?.content ?? [];
    // Latest version first; entries without a version sort to the end.
    return [...schedules].sort(
      (left, right) =>
        (right.version ?? -Infinity) - (left.version ?? -Infinity),
    )[0];
  }, [data]);

  const scheduleId = latestSchedule?.id ?? '';

  const { data: subscriptionItems, isLoading: isLoadingSubscriptionItems } =
    useGetRevenueScheduleSubscriptionItems(scheduleId);
  const { data: periods, isLoading: isLoadingPeriods } =
    useGetRevenueSchedulePeriods(scheduleId, {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  if ((!data && isLoading) || isLoadingSubscriptionItems || isLoadingPeriods) {
    return <MPageLoader />;
  }

  if (!latestSchedule) {
    return (
      <MBox pt={4}>
        <MText color="tGray.darkPurple" fontSize="sm" fontStyle="italic">
          No revenue schedules found for this revenue contract.
        </MText>
      </MBox>
    );
  }

  return (
    <MBox data-testid="revenue-contract-summary" pt={4}>
      <RevenueContractSummaryTable
        subscriptionItems={subscriptionItems ?? []}
        periods={periods ?? []}
      />
    </MBox>
  );
};
